/**
 * EditorialDataTable — the journal "figure + data" comparison table:
 * a mono small-caps header row over an ink underline, hairline-separated
 * rows, right-alignable numeric columns with tabular figures, and an
 * optional zebra wash.
 *
 * Editorial reference: the `table` rule in
 *   docs/design/round3-lcoe.html / round3-btm-study.html /
 *   round3-sentiment-risk-pathway.html / round3-recs-feasibility-finance.html:
 *     th  → mono, 9.5px, .12em tracking, uppercase, var(--muted),
 *           border-bottom: 1.5px solid var(--ink)   ← roleColor(theme,'ink')
 *     td  → border-bottom: 1px solid var(--hair)     ← roleColor(theme,'hair')
 *           a leading <b> cell reads var(--ink); numerics are mono.
 *
 * Used by:
 *   - LCOE (D2 fallback / D1 table)     — Technology × LCOE / timeline / land
 *   - BTM Technology (5A / 5B)          — capability table + suitability matrix
 *   - Risk Assessment (14B register)    — risk × likelihood / impact / mitigation
 *   - Pathway (16A decision matrix)     — criterion × pathway marks
 *   - Phased Finance (19A)              — phase × capex / opex / online
 *
 * Cells may carry React nodes (e.g. an <EditorialBadge> tag) so callers
 * compose status tags without the table owning tone logic.
 *
 * Theme tokens read: 'muted' (header ink), 'ink' (header underline + strong
 * cell ink), 'hair' (row rules), 'body' (default cell ink), 'subtleBg'
 * (optional zebra). Mono header + numerics via --report-body-font.
 * Pure / print-safe.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export type EditorialColumnAlign = 'left' | 'center' | 'right';

export interface EditorialTableColumn {
  /** Header label (small-caps mono). */
  header: React.ReactNode;
  /** Text alignment for the header + body cells. Defaults to 'left'. */
  align?: EditorialColumnAlign;
  /** Render this column's body cells in the mono / tabular numeric face. */
  numeric?: boolean;
  /** Optional fixed column width (CSS length). */
  width?: string | number;
  /**
   * Keep this column's HEADER label on a single line (`white-space:nowrap`).
   * Opt-in per column — the default remains wrapping (`normal`) so a wide
   * table still auto-fits the printable width without horizontal overflow.
   * Use for short, single-word headers (e.g. "Technology") that read worse
   * broken mid-word ("TECHNOLO/GY"); pair with an explicit `width` so the
   * fixed-layout table reserves the room the no-wrap label needs.
   */
  headerNoWrap?: boolean;
  /**
   * Keep this column's BODY cells on one line (`white-space:nowrap`).
   * Opt in only for compact values whose token must stay intact, such as an
   * LCOE value with its `/MWh` unit. Other columns continue to wrap normally.
   */
  cellNoWrap?: boolean;
}

export interface EditorialDataTableProps {
  theme: ReportTheme;
  columns: EditorialTableColumn[];
  /** Row data — each row is an array of cells aligned to `columns`. The
   *  FIRST cell renders in the strong 'ink' face (the row's name column). */
  rows: React.ReactNode[][];
  /** Zebra-stripe alternate rows with the subtle neutral wash. */
  zebra?: boolean;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Optional test-id forwarded to the root <table> element. */
  'data-testid'?: string;
}

export function EditorialDataTable({
  theme,
  columns,
  rows,
  zebra = false,
  id,
  className,
  style,
  'data-testid': dataTestId,
}: EditorialDataTableProps): React.ReactElement | null {
  if (rows.length === 0) return null;

  // SHRINK-TO-FIT (RC-1) — a long table is allowed to SPLIT across a page
  // break (continue + fill) instead of being pushed WHOLE to the next page
  // (which leaves a large white gap at the prior page foot). The `>= 7`
  // threshold guarantees a clean ≥2-row head + ≥2-row tail split is always
  // possible, so a split never strands a 1-row orphan/widow; tables with ≤6
  // rows stay ATOMIC (they fit on most pages and read as broken when split).
  // The header REPEATS on each continuation page via
  // `table thead { display: table-header-group }` and a row never splits
  // mid-cell via `table tr { break-inside: avoid }` — both added once in the
  // parity-gated reportDensityCss `shared` block (so the deck AND the
  // @media-print fallback honour them). The first/last body rows additionally
  // carry break-after/before:avoid (CSS orphans/widows do NOT govern table
  // rows) to keep ≥2 rows together at each split boundary.
  const splittable = rows.length >= 7;

  const dense = theme.density === 'compact';
  const muted = roleColor(theme, 'muted');
  const ink = roleColor(theme, 'ink');
  const hair = roleColor(theme, 'hair');
  const body = roleColor(theme, 'body');
  const subtle = roleColor(theme, 'subtleBg');

  const cellPad = dense ? '6px 8px' : '10px 12px';
  const headPad = dense ? '4px 8px' : '8px 12px';

  return (
    <table
      id={id}
      className={className}
      data-testid={dataTestId}
      data-editorial-table
      // SHRINK-TO-FIT — a long table opts INTO splitting; CSS (index.css +
      // the deck 2-col blanket) targets `[data-table-splittable="true"]` to
      // drop the atomic `break-inside:avoid` for exactly these tables.
      data-table-splittable={splittable ? 'true' : undefined}
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        // P3 — AUTO-FIT instead of clip: tableLayout:fixed honours explicit col
        // widths and equal-distributes the rest, so a wide table wraps within the
        // printable width instead of overflowing / clipping in the exported PDF.
        tableLayout: 'fixed',
        // SHRINK-TO-FIT — atomic only when SHORT; a long table fragments + fills
        // (header repeats, rows never split mid-cell — see the shared CSS rules).
        breakInside: splittable ? 'auto' : 'avoid',
        pageBreakInside: splittable ? 'auto' : 'avoid',
        // Unified report BODY size (~12.6px = 9.5pt, densified 2026-06-27 per
        // William) via --report-body-size; shrinks to 11.5px under @media print
        // via the [data-editorial-table] rule in index.css. The nested fallback
        // keeps the table tracking the body baseline even where the deck does
        // not cascade --report-body-size.
        fontSize: 'var(--editorial-table-fs, var(--report-body-size, 0.79rem))',
        // DEFECT 4 — SINGLE header rule. The table previously carried BOTH a
        // table `borderTop` AND the <th> `borderBottom`, so the header read as
        // a DOUBLE top line. Per the editorial reference (file header) the
        // header has ONE underline (the th border-bottom); the redundant table
        // top border is removed. The table foot rule (borderBottom) stays.
        borderBottom: `1.5px solid ${ink}`,
        marginBottom: '1rem',
        ...style,
      }}
    >
      <thead>
        <tr>
          {columns.map((col, c) => (
            <th
              key={c}
              scope="col"
              style={{
                fontFamily: MONO,
                // 12px label/caption token (text-xs) — unified editorial header.
                fontSize: REPORT_TEXT_PX.label,
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: muted,
                textAlign: col.align ?? 'left',
                padding: headPad,
                borderBottom: `1.5px solid ${ink}`,
                width: col.width,
                // P3 — wrap long headers instead of forcing horizontal overflow.
                // J5 — a column may opt INTO single-line headers (e.g. a short
                // "Technology" label that read worse broken mid-word as
                // "TECHNOLO/GY"); the default stays 'normal' so wide tables
                // still auto-fit without clipping.
                // FIX-A — the default wrap mode previously broke single words
                // MID-WORD ("TECHNOL/OGY", "CAPEX/M\nW") because
                // `overflow-wrap:anywhere` + `word-break:break-word` lets a
                // break land at ANY grapheme. Multi-word headers should wrap at
                // SPACES first and only break a single over-wide word as a last
                // resort — `overflow-wrap:break-word` + `word-break:normal` +
                // `hyphens:none` does exactly that (no gratuitous mid-word
                // fragment when the word fits the column). Pair an unavoidably
                // wide single-word header with `headerNoWrap`+`width` at the
                // call site to reserve the room it needs.
                whiteSpace: col.headerNoWrap ? 'nowrap' : 'normal',
                overflowWrap: col.headerNoWrap ? 'normal' : 'break-word',
                wordBreak: col.headerNoWrap ? 'keep-all' : 'normal',
                hyphens: 'none',
              }}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, r) => {
          // SHRINK-TO-FIT — ORPHAN/WIDOW ROW control (CSS orphans/widows do NOT
          // apply to table rows, so enforce structurally). When the table is
          // splittable, keep the first TWO body rows together (break-after:avoid
          // on row 0) and the last TWO body rows together (break-before:avoid on
          // the final row), so a split always leaves ≥2 rows on each side of
          // the page break — never a lonely 1-row head or tail. Applying either
          // keep to two adjacent rows would chain THREE rows and make Paged.js
          // backtrack to an unnecessarily early seam, leaving a large page tail.
          // Inert for short (atomic) tables.
          const keepStyle: React.CSSProperties = {
            // Paged.js 0.4.3 can still split a row while it clones a long
            // table into continuation fragments. Keep the
            // semantic row invariant on the row itself as well: a TR may
            // move to the next page, but its cells must never be sliced
            // into different fragment tables (which also produces an
            // invalid tagged-PDF column count).
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
            ...(splittable ? {
                ...(r === 0 ? { breakAfter: 'avoid', pageBreakAfter: 'avoid' } : {}),
                ...(r === rows.length - 1
                  ? { breakBefore: 'avoid', pageBreakBefore: 'avoid' }
                  : {}),
              } : {}),
          };
          const zebraStyle: React.CSSProperties | undefined =
            zebra && r % 2 === 1 ? { background: subtle } : undefined;
          return (
          <tr key={r} style={{ ...zebraStyle, ...keepStyle }}>
            {columns.map((col, c) => {
              const isName = c === 0;
              return (
                <td
                  key={c}
                  style={{
                    padding: cellPad,
                    verticalAlign: 'top',
                    borderBottom: `1px solid ${hair}`,
                    textAlign: col.align ?? 'left',
                    color: isName ? ink : body,
                    fontWeight: isName ? 600 : 400,
                    fontFamily: col.numeric ? MONO : undefined,
                    fontVariantNumeric: col.numeric ? 'tabular-nums' : undefined,
                    // P3 — wrap long cell content instead of clipping in print.
                    // Compact unit-bearing values can opt out via `cellNoWrap`
                    // so the browser never leaves a suffix orphaned (for example
                    // rendering "≈$164/MW" with the final "h" on a new line).
                    whiteSpace: col.cellNoWrap ? 'nowrap' : 'normal',
                    overflowWrap: col.cellNoWrap ? 'normal' : 'break-word',
                    wordBreak: col.cellNoWrap ? 'keep-all' : 'normal',
                    hyphens: 'none',
                  }}
                >
                  {row[c]}
                </td>
              );
            })}
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}
